/**
 * Self-service published-dashboard data pipeline.
 *
 * Each widget produced by the self-service builder carries its raw dataset
 * rows plus aggregation parameters in a `selfService` payload on the widget
 * config. At render/refilter time, ConfigService.getConfig delegates to this
 * utility, which:
 *   1. filters the raw rows by the active global / cross-filter selections,
 *   2. re-aggregates by the configured categoryKey/valueKey/aggregation,
 *   3. reshapes the result to match each chart component's expected input
 *      (bar/line keyed rows, literal {name, value} for pie, {bar: rows} for donut).
 *
 * This is the path that makes global filter dropdowns AND chart-click
 * cross-filters actually re-narrow the charts in the published view — both
 * flows feed through ChartWrapperService → onReload(filters) → getConfig().
 */

export type SelfServiceAggregation =
  | 'sum' | 'avg' | 'count' | 'distinctCount' | 'distinct'
  | 'min' | 'max' | 'median' | 'range'
  | 'first' | 'last' | string;

export type SelfServiceDateGranularity =
  'none' | 'day' | 'week' | 'month' | 'quarter' | 'year';

export type SelfServiceSortDirection = 'asc' | 'desc';
export type SelfServiceTopNDirection = 'top' | 'bottom';

export interface SelfServiceWidgetMeta {
  rawRows: Array<Record<string, unknown>>;
  columns?: Array<{ name: string; type?: string }>;
  chartType: string;
  categoryKey: string;
  valueKey: string;
  aggregation?: SelfServiceAggregation;
  sortBy?: string;
  sortDir?: SelfServiceSortDirection;
  topN?: number | null;
  topNDirection?: SelfServiceTopNDirection;
  dateGranularity?: SelfServiceDateGranularity;
  /** Optional: widgetId of the chart this metadata belongs to. Used to skip
   *  self-filtering when a chart click publishes a filter (the source chart
   *  shouldn't filter itself by its own click). */
  selfWidgetId?: string;
  /** Optional column whose distinct values break a multi-series chart
   *  (stacked / clustered / line-area / combo) into separate series. When
   *  absent, those charts render a single series named after the value column. */
  seriesField?: string;
  /** Raw x/y mapping for scatter & bubble family (no category aggregation). */
  xAxis?: string;
  yAxis?: string;
  /** Optional bubble size & color-by columns. */
  sizeKey?: string;
  colorByKey?: string;
  /** Column the KPI card reads its single value from. */
  kpiKey?: string;
  /** Ordered column names a data-table renders. */
  tableColumns?: string[];
  /** Explicit drill-down dimension (level) columns, in order. When absent the
   *  drill-down infers levels from the string columns of the dataset. */
  drillLevels?: string[];
  /** Explicit drill-down measure (numeric, summed) columns. When absent the
   *  drill-down infers measures from the numeric columns of the dataset. */
  drillMeasures?: string[];
}

export interface SelfServiceFilters {
  requestParams?: Record<string, unknown>;
  queryParams?: Record<string, unknown>;
  /** widgetId of the chart that triggered the most recent filter change.
   *  When equal to selfWidgetId on the meta, the corresponding key is skipped. */
  sourceWidgetId?: string | null;
}

/**
 * Chart types that consume a `{ category: string[], series: [{name, data}] }`
 * shape (stacked / clustered bars, stacked line/area, line-column, bar-line,
 * multi-bar-multi-line, multi-level comparison, multi-series combo). When a
 * `seriesField` is configured the rows are pivoted into one series per distinct
 * series value; otherwise a single series named after the value column is
 * produced so the chart still renders.
 */
const CATEGORY_SERIES_CHART_TYPES = new Set<string>([
  'stack-bar-chart',
  'generic-stack-bar-chart',
  'stack-bar-chart-generic',
  'mixed-stackbar-chart',
  'clustered-bar-chart',
  'stacked-100-bar-chart',
  'stacked-line-chart',
  'stacked-area-chart',
  'combined-chart',
  'bar-line',
  'mbar-mline',
  'multi-level-comparison-chart',
]);

/** Stacked-horizontal reads { categories, series:[{name,data}] } but checks a
 *  `bar` array first (which routes into a domain-specific cummins/non-cummins
 *  path that bails without that config). So it must NOT receive a `bar` field. */
const HORIZONTAL_STACK_CHART_TYPES = new Set<string>([
  'stacked-horizontal-bar-chart',
  'horizontal-bar-chart-segment',
]);

/** Line-column reads number arrays at config-named paths (xAxis/barSeries/
 *  lineSeries). It needs a flat object, not { category, series }. */
const LINE_COLUMN_CHART_TYPES = new Set<string>(['line-column-chart']);

/** The multi-series bar-line combo reads a flat row ARRAY (payload.data or
 *  payload) driven by barSeries/lineSeries field config. */
const COMBO_CHART_TYPES = new Set<string>(['multi-series-bar-line-combo-chart']);

/** Plain waterfall: the component spreads each series entry into ECharts, so it
 *  needs the full [Placeholder, Increase, Decrease] series structure. */
const WATERFALL_CHART_TYPES = new Set<string>(['waterfall-chart']);

/** Waterfall-stack (generic mode): renders a stacked waterfall from a plain
 *  { category, series:[{name,data}] } payload. */
const WATERFALL_STACK_CHART_TYPES = new Set<string>(['waterfall-stack']);

/** Scatter / bubble family: needs { points: [...] } from raw x/y rows. */
const BUBBLE_CHART_TYPES = new Set<string>([
  'scatter-plot',
  'bubble-chart',
  'configurable-multi-series-scatter-chart',
  'multi-series-scatter-chart',
]);

/** Quadrant bubble: needs { q1, q2, q3, q4 } — points bucketed into four
 *  quadrants split by the median of x and y. */
const QUAD_BUBBLE_CHART_TYPES = new Set<string>(['quad-bubble-chart']);

export function computeSelfServiceData(
  meta: SelfServiceWidgetMeta,
  filters?: SelfServiceFilters,
): unknown {
  const filteredRows = applyFilters(meta.rawRows, meta.columns, filters, meta);
  const type = meta.chartType;

  // --- Data table: { options: rows[] } limited to the chosen columns. ---
  if (type === 'data-table') {
    return reshapeTable(filteredRows, meta);
  }

  // --- Drill-down tree table: flat rows + inferred level/measure columns.
  // The GenericDrillDownComponent groups the rows into a hierarchy client-side,
  // so we hand it the filtered rows plus which columns are dimensions (string)
  // and which are measures (numeric). ---
  if (type === 'generic-drill-down') {
    return reshapeDrillDown(filteredRows, meta);
  }

  // --- KPI card: single aggregated scalar under meta.kpiKey. ---
  if (type === 'kpi-card') {
    return reshapeKpi(filteredRows, meta);
  }

  // --- Treemap: [{ name, value, children:[{name,value}] }] hierarchy. ---
  if (type === 'treemap-chart') {
    return reshapeTreemap(filteredRows, meta);
  }

  // --- Gantt: task records [{ name, start, end, group }]. ---
  if (type === 'gantt-chart') {
    return reshapeGantt(filteredRows, meta);
  }

  // --- Quadrant bubble: { q1, q2, q3, q4 } split by median(x), median(y). ---
  if (QUAD_BUBBLE_CHART_TYPES.has(type)) {
    return reshapeQuadBubble(filteredRows, meta);
  }

  // --- Scatter / bubble family: { points: [...] } from raw x/y rows. ---
  if (BUBBLE_CHART_TYPES.has(type)) {
    return reshapeBubble(filteredRows, meta);
  }

  // --- Plain waterfall: [Placeholder, Increase, Decrease] ECharts series. ---
  if (WATERFALL_CHART_TYPES.has(type)) {
    return reshapeWaterfall(filteredRows, meta);
  }

  // --- Waterfall-stack: generic { category, series:[{name,data}] }. ---
  if (WATERFALL_STACK_CHART_TYPES.has(type)) {
    const shaped = reshapeCategorySeries(filteredRows, meta) as {
      category: string[]; series: Array<{ name: string; data: number[] }> };
    return { category: shaped.category, series: shaped.series };
  }

  // --- Line-column: { [xAxisPath]: labels, [barPath]: nums, [linePath]: nums }. ---
  if (LINE_COLUMN_CHART_TYPES.has(type)) {
    return reshapeLineColumn(filteredRows, meta);
  }

  // --- Multi-series bar-line combo: flat row array under `data`. ---
  if (COMBO_CHART_TYPES.has(type)) {
    return reshapeCombo(filteredRows, meta);
  }

  // --- Stacked-horizontal: { categories, series } WITHOUT a `bar` field. ---
  if (HORIZONTAL_STACK_CHART_TYPES.has(type)) {
    const shaped = reshapeCategorySeries(filteredRows, meta) as {
      categories: string[]; series: Array<Record<string, unknown>>;
    };
    return { categories: shaped.categories, series: shaped.series };
  }

  // --- Multi-series category charts: { category: [...], series:[{name,data}] }. ---
  if (CATEGORY_SERIES_CHART_TYPES.has(type)) {
    return reshapeCategorySeries(filteredRows, meta);
  }

  // --- Simple single-value charts (bar, line, horizontal-bar, pie, donut). ---
  if (!meta.categoryKey || !meta.valueKey) {
    return filteredRows.map((row) => ({ ...row }));
  }

  const aggregated = aggregateByCategory(filteredRows, meta);

  if (type === 'pie-chart') {
    return aggregated.map(({ name, value }) => ({ name, value }));
  }

  const keyedRows = aggregated.map(({ name, value }) => ({
    [meta.categoryKey]: name,
    [meta.valueKey]: value,
  }));

  if (type === 'donut-chart') {
    return { bar: keyedRows };
  }

  return keyedRows;
}

/**
 * Return the RAW rows of a self-service widget narrowed by the current global /
 * cross-filter selections — WITHOUT any chart-type reshaping. The published
 * SelfServiceEChartsComponent feeds these into the shared chart-option builder
 * so it can rebuild the exact builder-preview option from freshly filtered data.
 */
export function filterSelfServiceRows(
  meta: SelfServiceWidgetMeta,
  filters?: SelfServiceFilters,
): Array<Record<string, unknown>> {
  return applyFilters(meta.rawRows, meta.columns, filters, meta);
}

/** Unit-separator control char for composite Map keys; a rare separator that
 *  never collides with user-facing data labels (which may contain spaces and
 *  dashes, but not control characters). */
const CELL_SEP = '\u001F';

function reshapeCategorySeries(
  rows: Array<Record<string, unknown>>,
  meta: SelfServiceWidgetMeta,
): unknown {
  const catKey = meta.categoryKey;
  const valKey = meta.valueKey;
  if (!catKey || !valKey) return { category: [], categories: [], series: [], bar: [] };

  const bucketed = applyDateGranularity(rows, catKey, meta.dateGranularity);
  const seriesKey = meta.seriesField && meta.seriesField !== catKey ? meta.seriesField : '';
  const aggregation = meta.aggregation || 'sum';

  // Preserve first-seen order of categories and series.
  const categories: string[] = [];
  const seriesNames: string[] = [];
  // cell key = `${seriesName}${CELL_SEP}${category}` -> raw values to aggregate.
  // A rare control-char separator avoids collisions when labels contain spaces
  // (e.g. "North America", "Q1 2023").
  const cells = new Map<string, number[]>();
  const cellKeyOf = (series: string, cat: string): string => `${series}${CELL_SEP}${cat}`;

  const labelOf = (raw: unknown): string =>
    raw === undefined || raw === null || String(raw).trim() === '' ? '(blank)' : String(raw);

  const singleSeriesName = valKey;
  bucketed.forEach((row) => {
    const cat = labelOf(row[catKey]);
    const seriesName = seriesKey ? labelOf(row[seriesKey]) : singleSeriesName;
    if (!categories.includes(cat)) categories.push(cat);
    if (!seriesNames.includes(seriesName)) seriesNames.push(seriesName);
    const cellKey = cellKeyOf(seriesName, cat);
    const arr = cells.get(cellKey) || [];
    arr.push(toNumber(row[valKey]));
    cells.set(cellKey, arr);
  });

  // Sort / topN operate on categories by their total across series.
  const orderedCategories = applyCategoryShape(categories, seriesNames, cells, meta);

  const valueAt = (name: string, cat: string): number => {
    const arr = cells.get(cellKeyOf(name, cat));
    return arr && arr.length ? aggregateValues(arr, aggregation) : 0;
  };

  // Pre-formatted series carry both `data` and `value` because different
  // components in this family read one or the other field name.
  const series = seriesNames.map((name) => {
    const dataArr = orderedCategories.map((cat) => valueAt(name, cat));
    return { name, type: 'bar', data: dataArr, value: dataArr };
  });

  // Flat rows for components that pivot raw rows themselves (read `bar`/array).
  const seriesColName = seriesKey || 'series';
  const bar: Array<Record<string, unknown>> = [];
  orderedCategories.forEach((cat) => {
    seriesNames.forEach((name) => {
      bar.push({ [catKey]: cat, [seriesColName]: name, [valKey]: valueAt(name, cat) });
    });
  });

  // UNION shape so every component in this family finds a branch it understands:
  // { category, categories } x-axis labels, pre-formatted `series`, and a flat
  // `bar` array for components that pivot raw rows themselves.
  return { category: orderedCategories, categories: orderedCategories, series, bar };
}

/**
 * Line-column: the component resolves number arrays at the config-named paths
 * data.xAxis / data.barSeries / data.lineSeries. The self-service builder pins
 * those config keys to the literal names used here ('category' / 'barValues' /
 * 'lineValues'). With a single value mapping there's one metric, so the line
 * mirrors the bar (a smooth overlay of the same magnitude) rather than a
 * second, unavailable measure.
 */
function reshapeLineColumn(
  rows: Array<Record<string, unknown>>,
  meta: SelfServiceWidgetMeta,
): Record<string, unknown> {
  if (!meta.categoryKey || !meta.valueKey) {
    return { category: [], barValues: [], lineValues: [] };
  }
  const aggregated = aggregateByCategory(rows, meta);
  const values = aggregated.map((a) => a.value);
  return {
    category: aggregated.map((a) => a.name),
    barValues: values,
    lineValues: values,
  };
}

/**
 * Multi-series bar-line combo: the component reads a flat row ARRAY (payload.data
 * or payload) and builds bar/line series from barSeries/lineSeries field config.
 * We emit one row per category with the aggregated value under `value`; the
 * builder configures a single bar series (valueField: 'value') plus a mirrored
 * line series so the combo renders meaningfully from a single value mapping.
 */
function reshapeCombo(
  rows: Array<Record<string, unknown>>,
  meta: SelfServiceWidgetMeta,
): Record<string, unknown> {
  if (!meta.categoryKey || !meta.valueKey) return { data: [] };
  const aggregated = aggregateByCategory(rows, meta);
  return {
    data: aggregated.map((a) => ({ category: a.name, value: a.value })),
  };
}

/** Apply sortBy / topN to the category axis using per-category totals. */
function applyCategoryShape(
  categories: string[],
  seriesNames: string[],
  cells: Map<string, number[]>,
  meta: SelfServiceWidgetMeta,
): string[] {
  const totalOf = (cat: string): number =>
    seriesNames.reduce((sum, name) => {
      const arr = cells.get(`${name}${CELL_SEP}${cat}`);
      return sum + (arr && arr.length ? aggregateValues(arr, meta.aggregation || 'sum') : 0);
    }, 0);

  let result = [...categories];
  if (meta.sortBy) {
    const dir = meta.sortDir === 'desc' ? -1 : 1;
    const byName = meta.sortBy === 'name';
    result.sort((a, b) =>
      byName ? a.localeCompare(b) * dir : (totalOf(a) - totalOf(b)) * dir,
    );
  }
  if (meta.topN && meta.topN > 0) {
    const ranked = meta.sortBy ? result : [...result].sort((a, b) => totalOf(b) - totalOf(a));
    result = meta.topNDirection === 'bottom' ? ranked.slice(-meta.topN) : ranked.slice(0, meta.topN);
  }
  return result;
}

/**
 * Waterfall: the component spreads each `series` entry straight into an ECharts
 * series, so we must emit the full ECharts waterfall structure — a transparent
 * Placeholder bar carrying the running balance, plus separate Increase/Decrease
 * bars stacked on top. This mirrors the build-side preview's buildWaterfallOption.
 */
function reshapeWaterfall(
  rows: Array<Record<string, unknown>>,
  meta: SelfServiceWidgetMeta,
): { category: string[]; series: Array<Record<string, unknown>> } {
  if (!meta.categoryKey || !meta.valueKey) return { category: [], series: [] };
  const aggregated = aggregateByCategory(rows, meta);
  const categories = aggregated.map((a) => a.name);
  const values = aggregated.map((a) => Number(a.value) || 0);

  const placeholder: Array<number | string> = [];
  const increase: Array<number | string> = [];
  const decrease: Array<number | string> = [];
  let running = 0;
  for (const value of values) {
    if (value >= 0) {
      placeholder.push(running);
      increase.push(value);
      decrease.push('-');
    } else {
      placeholder.push(running + value);
      increase.push('-');
      decrease.push(-value);
    }
    running += value;
  }

  const series: Array<Record<string, unknown>> = [
    {
      name: 'Placeholder',
      type: 'bar',
      stack: 'total',
      silent: true,
      itemStyle: { color: 'transparent', borderColor: 'transparent' },
      emphasis: { itemStyle: { color: 'transparent', borderColor: 'transparent' } },
      data: placeholder,
    },
    { name: 'Increase', type: 'bar', stack: 'total', itemStyle: { color: '#10b981' }, data: increase },
    { name: 'Decrease', type: 'bar', stack: 'total', itemStyle: { color: '#ef4444' }, data: decrease },
  ];

  return { category: categories, series };
}

/** Scatter / bubble: { points: [[x, y, size, label], ...] } from raw rows. */
function reshapeBubble(
  rows: Array<Record<string, unknown>>,
  meta: SelfServiceWidgetMeta,
): { points: Array<[number, number, number, string]> } {
  const xKey = meta.xAxis || meta.categoryKey;
  const yKey = meta.yAxis || meta.valueKey;
  const sizeKey = meta.sizeKey;
  const labelKey = meta.colorByKey || meta.seriesField;
  const points = rows.map((row): [number, number, number, string] => {
    const x = toNumber(row[xKey]);
    const y = toNumber(row[yKey]);
    const size = sizeKey ? toNumber(row[sizeKey]) : Math.max(Math.abs(x), Math.abs(y), 1);
    const label = labelKey ? String(row[labelKey] ?? '') : '';
    return [x, y, size, label];
  });
  return { points };
}

/**
 * Quadrant bubble: split points into { q1, q2, q3, q4 } by the median of x and
 * y (classic quadrant analysis — low/high X × low/high Y). The component colors
 * each quadrant as a distinct cluster. Quadrant indexing matches the component's
 * Q1..Q4 → cluster mapping: Q1 = high-x/high-y, Q2 = low-x/high-y,
 * Q3 = low-x/low-y, Q4 = high-x/low-y.
 */
function reshapeQuadBubble(
  rows: Array<Record<string, unknown>>,
  meta: SelfServiceWidgetMeta,
): { q1: unknown[]; q2: unknown[]; q3: unknown[]; q4: unknown[] } {
  const xKey = meta.xAxis || meta.categoryKey;
  const yKey = meta.yAxis || meta.valueKey;
  const sizeKey = meta.sizeKey;
  const labelKey = meta.colorByKey || meta.seriesField;

  const pts = rows.map((row) => ({
    x: toNumber(row[xKey]),
    y: toNumber(row[yKey]),
    size: sizeKey ? toNumber(row[sizeKey]) : 15,
    label: labelKey ? String(row[labelKey] ?? '') : '',
  }));

  const median = (values: number[]): number => {
    if (!values.length) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  };
  const mx = median(pts.map((p) => p.x));
  const my = median(pts.map((p) => p.y));

  const q1: unknown[] = [];
  const q2: unknown[] = [];
  const q3: unknown[] = [];
  const q4: unknown[] = [];
  pts.forEach((p) => {
    const point = { x: p.x, y: p.y, size: p.size, label: p.label, name: p.label };
    if (p.x >= mx && p.y >= my) q1.push(point);
    else if (p.x < mx && p.y >= my) q2.push(point);
    else if (p.x < mx && p.y < my) q3.push(point);
    else q4.push(point);
  });

  return { q1, q2, q3, q4 };
}

/** Treemap: group rows by category, summing value, into a one-level tree. */
function reshapeTreemap(
  rows: Array<Record<string, unknown>>,
  meta: SelfServiceWidgetMeta,
): Array<{ name: string; value: number; children: Array<{ name: string; value: number }> }> {
  if (!meta.categoryKey || !meta.valueKey) {
    // No mapping: fall back to raw rows so the component can still try.
    return [];
  }
  const aggregated = aggregateByCategory(rows, meta).filter((a) => a.value > 0);
  // Single-level treemap: each category is a leaf. Wrap as a node with one
  // child so the component's parent/child renderer has a consistent shape.
  return aggregated.map((a) => ({
    name: a.name,
    value: a.value,
    children: [{ name: a.name, value: a.value }],
  }));
}

/** KPI card: aggregate the chosen value column into a single scalar payload. */
function reshapeKpi(
  rows: Array<Record<string, unknown>>,
  meta: SelfServiceWidgetMeta,
): Record<string, number | string> {
  const key = meta.kpiKey || meta.valueKey;
  if (!key) return { value: 0 };

  const aggregation = meta.aggregation || 'sum';
  const columnType = meta.columns?.find((column) => column.name === key)?.type;
  const rawValues = rows.map((row) => String(row[key] ?? '').trim());
  const nonEmptyValues = rawValues.filter((value) => value !== '');
  const numericResult = columnType === 'number' || aggregation === 'count' || aggregation === 'distinctCount';

  if (!numericResult) {
    if (aggregation === 'distinct') {
      return { [key]: Array.from(new Set(nonEmptyValues)).join(', ') };
    }
    if (aggregation === 'last') {
      return { [key]: nonEmptyValues[nonEmptyValues.length - 1] || '' };
    }
    return { [key]: nonEmptyValues[0] || '' };
  }

  const values = rawValues.map((value) => toNumber(value));
  return { [key]: aggregateValues(values, aggregation) };
}
/**
 * Drill-down tree table: returns the filtered flat rows plus the resolved
 * level (dimension) and measure (numeric) columns. The component builds the
 * nested hierarchy itself. Level order follows the configured column order;
 * string columns become levels, numeric columns become summed measures. If the
 * config carries explicit `drillLevels` / `drillMeasures` those win.
 */
function reshapeDrillDown(
  rows: Array<Record<string, unknown>>,
  meta: SelfServiceWidgetMeta,
): {
  options: Array<Record<string, unknown>>;
  levelColumns: string[];
  measureColumns: string[];
} {
  const allCols = meta.tableColumns?.length
    ? meta.tableColumns
    : meta.columns?.map((c) => c.name) ?? [];

  let levelColumns = meta.drillLevels?.length ? meta.drillLevels : [];
  let measureColumns = meta.drillMeasures?.length ? meta.drillMeasures : [];

  if (!levelColumns.length || !measureColumns.length) {
    // Classify columns by the dataset's declared type first, falling back to a
    // value sniff on the first row. Numeric → measure, everything else → level.
    const typeByName = new Map((meta.columns || []).map((c) => [c.name, c.type]));
    const sample = rows.find((r) => r && typeof r === 'object') || {};
    const levels: string[] = [];
    const measures: string[] = [];
    for (const col of allCols) {
      const declared = (typeByName.get(col) || '').toLowerCase();
      const isNumeric = declared
        ? declared === 'number' || declared === 'numeric' || declared === 'integer' || declared === 'float'
        : Number.isFinite(toNumber(sample[col]));
      if (isNumeric) measures.push(col);
      else levels.push(col);
    }
    if (!levelColumns.length) levelColumns = levels;
    if (!measureColumns.length) measureColumns = measures;
  }

  const limited = meta.topN && meta.topN > 0 ? rows.slice(0, meta.topN) : rows;
  return {
    options: limited.map((row) => ({ ...row })),
    levelColumns,
    measureColumns,
  };
}

/** Data table: { options: rows[] } projected to the configured columns. */
function reshapeTable(
  rows: Array<Record<string, unknown>>,
  meta: SelfServiceWidgetMeta,
): { options: Array<Record<string, unknown>> } {
  const cols = meta.tableColumns?.length
    ? meta.tableColumns
    : meta.columns?.map((c) => c.name) ?? [];
  const limited = meta.topN && meta.topN > 0 ? rows.slice(0, meta.topN) : rows;
  if (!cols.length) {
    return { options: limited.map((row) => ({ ...row })) };
  }
  return {
    options: limited.map((row) => {
      const projected: Record<string, unknown> = {};
      cols.forEach((c) => (projected[c] = row[c]));
      return projected;
    }),
  };
}

/**
 * Gantt: aggregate per category into task records the gantt component reads
 * (name / start / end / group). Mirrors the builder's static fallback logic:
 * real year/timestamp values map to ISO dates; otherwise tasks are laid out on
 * a synthetic consecutive-month timeline.
 */
function reshapeGantt(
  rows: Array<Record<string, unknown>>,
  meta: SelfServiceWidgetMeta,
): Array<{ name: string; start: string; end: string; group: string }> {
  const startKey = meta.xAxis || meta.categoryKey;
  const endKey = meta.yAxis || meta.valueKey;
  const catKey = meta.seriesField || startKey;
  if (!startKey) return [];
  const sameCol = startKey === endKey;

  const groups = new Map<string, { start: number; end: number }>();
  rows.forEach((row) => {
    const cat = String(row[catKey] ?? '(blank)');
    const rawA = toNumber(row[startKey]);
    const rawB = toNumber(row[endKey]);
    const s = sameCol ? rawA : Math.min(rawA, rawB);
    const e = sameCol ? rawA + 1 : Math.max(rawA, rawB);
    if (e <= s) return;
    const existing = groups.get(cat);
    if (!existing) groups.set(cat, { start: s, end: e });
    else {
      existing.start = Math.min(existing.start, s);
      existing.end = Math.max(existing.end, e);
    }
  });

  const startVals = Array.from(groups.values()).map((r) => r.start);
  const haveMeaningfulDates =
    startVals.length > 0 && startVals.every((v) => (v >= 1900 && v <= 2100) || v > 1e8);

  if (haveMeaningfulDates) {
    return Array.from(groups.entries())
      .filter(([, r]) => r.end > r.start)
      .map(([name, r]) => ({
        name,
        start: toGanttDateString(r.start),
        end: toGanttDateString(r.end),
        group: '',
      }));
  }

  const MONTH_ABBR = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const monthIdx = (n: string): number => MONTH_ABBR.findIndex((m) => n.toLowerCase().startsWith(m));
  const baseYear = new Date().getFullYear();
  const sorted = Array.from(groups.entries()).sort(([nA, a], [nB, b]) => {
    const mA = monthIdx(nA);
    const mB = monthIdx(nB);
    return mA >= 0 && mB >= 0 ? mA - mB : a.start - b.start;
  });
  return sorted.map(([name], idx) => {
    const m = idx % 12;
    const yr = baseYear + Math.floor(idx / 12);
    const nm = (m + 1) % 12;
    const nyr = m === 11 ? yr + 1 : yr;
    return {
      name,
      start: `${yr}-${String(m + 1).padStart(2, '0')}-01`,
      end: `${nyr}-${String(nm + 1).padStart(2, '0')}-01`,
      group: name,
    };
  });
}

/** Convert a numeric value (year or unix-seconds) to an ISO YYYY-MM-DD string. */
function toGanttDateString(value: number): string {
  // Year-like values become Jan 1 of that year; large values are unix seconds.
  const d = value >= 1900 && value <= 2100
    ? new Date(value, 0, 1)
    : new Date(value * 1000);
  if (Number.isNaN(d.getTime())) return '1970-01-01';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function applyFilters(
  rows: Array<Record<string, unknown>>,
  columns: Array<{ name: string; type?: string }> | undefined,
  filters: SelfServiceFilters | undefined,
  meta: SelfServiceWidgetMeta,
): Array<Record<string, unknown>> {
  if (!filters) return rows;

  const columnNames = columns?.length
    ? new Set(columns.map((c) => c.name))
    : null;
  const filterEntries: Array<[string, Set<string>]> = [];

  const collect = (params: Record<string, unknown> | undefined): void => {
    if (!params) return;
    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === undefined || value === '') continue;
      if (columnNames && !columnNames.has(key)) continue;
      const values = (Array.isArray(value) ? value : [value])
        .map((v) => String(v ?? '').trim())
        .filter((v) => v !== '');
      if (!values.length) continue;
      filterEntries.push([key, new Set(values)]);
    }
  };
  collect(filters.requestParams);
  collect(filters.queryParams);

  if (!filterEntries.length) return rows;

  return rows.filter((row) =>
    filterEntries.every(([key, valueSet]) => valueSet.has(String(row[key] ?? ''))),
  );
}

function aggregateByCategory(
  rows: Array<Record<string, unknown>>,
  meta: SelfServiceWidgetMeta,
): Array<{ name: string; value: number }> {
  const datasetRows = applyDateGranularity(rows, meta.categoryKey, meta.dateGranularity);

  const grouped = new Map<string, number[]>();
  datasetRows.forEach((row) => {
    const raw = row[meta.categoryKey];
    const category = raw === undefined || raw === null || String(raw).trim() === ''
      ? '(blank)'
      : String(raw);
    const numbers = grouped.get(category) || [];
    numbers.push(toNumber(row[meta.valueKey]));
    grouped.set(category, numbers);
  });

  const items = Array.from(grouped.entries()).map(([name, values]) => ({
    name,
    value: aggregateValues(values, meta.aggregation || 'sum'),
  }));

  return applyShape(items, meta);
}

function applyShape(
  items: Array<{ name: string; value: number }>,
  meta: SelfServiceWidgetMeta,
): Array<{ name: string; value: number }> {
  let result = items;
  if (meta.sortBy) {
    const dir = meta.sortDir === 'desc' ? -1 : 1;
    result = [...result].sort((a, b) => {
      const useValue = meta.sortBy !== 'name';
      return useValue
        ? (a.value - b.value) * dir
        : a.name.localeCompare(b.name) * dir;
    });
  }
  if (meta.topN && meta.topN > 0) {
    const sortedForLimit = meta.sortBy
      ? result
      : [...result].sort((a, b) => b.value - a.value);
    result = meta.topNDirection === 'bottom'
      ? sortedForLimit.slice(-meta.topN)
      : sortedForLimit.slice(0, meta.topN);
  }
  return result;
}

function toNumber(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const s = String(value ?? '').replace(/,/g, '').trim();
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function aggregateValues(values: number[], aggregation: SelfServiceAggregation): number {
  if (aggregation === 'count') return values.length;
  if (aggregation === 'distinctCount' || aggregation === 'distinct') return new Set(values).size;
  if (!values.length) return 0;
  if (aggregation === 'avg') return values.reduce((a, b) => a + b, 0) / values.length;
  if (aggregation === 'min') return Math.min(...values);
  if (aggregation === 'max') return Math.max(...values);
  if (aggregation === 'range') return Math.max(...values) - Math.min(...values);
  if (aggregation === 'first') return values[0];
  if (aggregation === 'last') return values[values.length - 1];
  if (aggregation === 'median') {
    const sorted = [...values].sort((a, b) => a - b);
    const rank = 0.5 * (sorted.length - 1);
    const lo = Math.floor(rank);
    const hi = Math.ceil(rank);
    return lo === hi ? sorted[lo] : sorted[lo] + (sorted[hi] - sorted[lo]) * (rank - lo);
  }
  // Default + 'sum' + any unsupported custom aggregation key (e.g. 'custom:foo')
  // fall back to sum so the chart still renders something coherent.
  return values.reduce((a, b) => a + b, 0);
}

function applyDateGranularity(
  rows: Array<Record<string, unknown>>,
  xKey: string,
  granularity: SelfServiceDateGranularity | undefined,
): Array<Record<string, unknown>> {
  if (!granularity || granularity === 'none' || !xKey) return rows;
  const bucket = (raw: unknown): string => {
    const d = new Date(String(raw ?? ''));
    if (Number.isNaN(d.getTime())) return String(raw ?? '');
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const day = d.getDate();
    switch (granularity) {
      case 'year': return String(y);
      case 'quarter': return `${y}-Q${Math.floor((m - 1) / 3) + 1}`;
      case 'month': return `${y}-${String(m).padStart(2, '0')}`;
      case 'week': {
        const start = new Date(d);
        start.setDate(d.getDate() - d.getDay());
        return `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
      }
      case 'day':
      default: return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  };
  return rows.map((row) => ({ ...row, [xKey]: bucket(row[xKey]) }));
}
